// offlineReferenceCore — 離線查工項／比對編碼規則的純函式（2026-10-01，從 offlineReference.js 拆出）。
//
// 只 import 同樣是純函式的 itemCodeParse.js，不碰 axios／IndexedDB／Vue，tests/unit 可以直接測。
// 處理器登記與本機資料存取在 Common/offlineReference.js。
//
// ⚠⚠⚠ 同構配對（前端純 JS 對後端 C#，天生不能共用原始碼）：
//   evaluateRule／buildRuleResult ↔ BudgetItemPickerService.EvaluateRuleCore／BuildRuleResult
//     ── tests/fixtures/offline-reference/ruleCases.json 兩邊共用，期望值由 C# 產生
//        （tests/Pcces.Tests/OfflineReferenceRuleCrossTests.cs ＋ tests/unit/offlineReference.test.mjs）
//   digitsFromTable ↔ BudgetCheckService.LoadDigits（規則表 steps → 碼位；同一碼位所有選項聯集、
//     不看父代碼、空代碼不算）
//   searchPccItems ↔ BudgetItemPickerService.SearchPccItemsAsync（四種篩法互斥、依代碼排序、分頁）
//   findExactItem ↔ BudgetItemPickerService.QueryExactAsync（代碼完全相同）
//   改一邊一定要改另一邊。
// ⚠ 已知落差：LoadDigits 的 SQL 沒有指定選項順序，同一碼位裡「代碼相同、名稱不同」時取哪個名稱本來
//   就不固定；這裡照規則表的固定順序取最後一個。只影響這種極少見情況下提示顯示的名稱，不影響合不合法。

import { parseItemCode } from './itemCodeParse.js'

/** BudgetItemPickerService.SanitizeCodePrefix：1~20 個 ASCII 英數字，否則 null。 */
export function sanitizeCodePrefix(raw) {
  const t = String(raw ?? '').trim()
  if (t.length === 0 || t.length > 20) return null
  return /^[A-Za-z0-9]+$/.test(t) ? t : null
}

/** SQL Server Latin1_General_CI_AS 的近似：不分大小寫、不分全形半形（ASCII 範圍）。 */
export function foldForSearch(s) {
  return String(s ?? '')
    .replace(/[！-～]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
    .replace(/　/g, ' ')
    .toLowerCase()
}

const byCodeOrdinal = (a, b) => (a.itemCode < b.itemCode ? -1 : a.itemCode > b.itemCode ? 1 : 0)
const VALID_KINDS = ['E', 'L', 'M', 'W']

/** BudgetItemPickerService.SearchPccItemsAsync 的本機版：四種篩法互斥、依代碼排序、分頁。 */
export function searchPccItems(body, items) {
  const q = body || {}
  const pageSize = q.pageRecordCount > 0 ? Math.min(q.pageRecordCount, 200) : 12
  const pageIndex = q.pageIndex > 0 ? q.pageIndex : 1
  const keyword = String(q.keyword ?? '').trim()
  const outline = String(q.outlineCode ?? '').trim()
  const kindRaw = String(q.kind ?? '').trim()
  const kind = VALID_KINDS.includes(kindRaw) ? kindRaw : null
  const codePrefix = sanitizeCodePrefix(q.codePrefix)
  if (!keyword && !outline && !kind && !codePrefix) return { items: [], total: 0 }

  const kw = foldForSearch(keyword)
  const kwMatch = (it) => foldForSearch(it.itemCode).includes(kw) || foldForSearch(it.nameZh).includes(kw)
  let filter
  if (codePrefix) filter = (it) => it.itemCode.startsWith(codePrefix)
  else if (kind) filter = (it) => it.codeKind === kind && (!keyword || kwMatch(it))
  else if (keyword) filter = kwMatch
  else filter = (it) => it.itemCode.slice(0, 5) === outline

  const matched = (items || []).filter(filter).sort(byCodeOrdinal)
  return {
    items: matched.slice((pageIndex - 1) * pageSize, pageIndex * pageSize).map((it) => ({
      itemCode: it.itemCode, nameZh: it.nameZh ?? '', unit: it.unit ?? '', price: it.unitPrice ?? 0,
    })),
    total: matched.length,
  }
}

/** BudgetItemPickerService.QueryExactAsync 的本機版：代碼完全相同（分大小寫）。 */
export function findExactItem(code, items) {
  const c = String(code ?? '').trim()
  if (!c) return null
  const it = (items || []).find((x) => x.itemCode === c)
  if (!it) return null
  return {
    itemCode: it.itemCode, nameZh: it.nameZh ?? '', unit: it.unit ?? '', unitPrice: it.unitPrice,
    laborRate: it.laborRate, equipRate: it.equipRate, materialRate: it.materialRate, miscRate: it.miscRate,
  }
}

/**
 * 規則表（GetRuleTable 形狀）→ 規則比對用的碼位（BudgetCheckService.LoadDigits 的形狀）：
 * 每個碼位的合法代碼＝這一碼所有選項（平的 options 或各父代碼分組的 options 全部聯集）。
 */
export function digitsFromTable(rule) {
  if (!rule || rule.noRuleTable) return []
  return (rule.steps || []).map((st) => {
    const codeNames = new Map()
    const add = (o) => { if (o && o.c) codeNames.set(o.c, o.n ?? '') }
    ;(st.options || []).forEach(add)
    ;(st.groups || []).forEach((g) => (g.options || []).forEach(add))
    return { digit: st.digit, span: st.span ?? 1, title: st.title ?? '', codeNames }
  })
}

const emptyRule = (chapter = '') => ({
  hasRule: false, valid: false, chapter, digits: [], unit: null, rulePrefix: '', prefixMismatch: false, prefixMessage: '',
})

/** BudgetItemPickerService.BuildRuleResult：逐碼比對＋長度＋前綴。 */
export function buildRuleResult(code, chapter, digits, codePrefix = '', rulePrefix = '') {
  const result = emptyRule(chapter)
  if (!digits.length) return result
  result.hasRule = true
  result.rulePrefix = rulePrefix
  const prefixOk = codePrefix === rulePrefix
  result.prefixMismatch = !prefixOk
  if (!prefixOk) {
    result.prefixMessage = !rulePrefix
      ? `此章碼編碼規則沒有前綴字母，代碼不應該帶「${codePrefix}」前綴`
      : `此章碼編碼規則帶前綴 ${rulePrefix}，代碼應為 ${rulePrefix}${chapter}…`
  }
  const prefixLen = codePrefix.length
  const maxEnd = prefixLen + Math.max(...digits.map((d) => (d.digit - 1) + d.span))
  let valid = code.length === maxEnd && prefixOk
  for (const d of [...digits].sort((a, b) => a.digit - b.digit)) {
    const start = prefixLen + (d.digit - 1)
    if (start < 0 || start + d.span > code.length) { valid = false; continue }
    const value = code.slice(start, start + d.span)
    const name = d.codeNames.has(value) ? d.codeNames.get(value) : ''
    if (!d.codeNames.has(value)) valid = false
    result.digits.push({ position: d.digit, code: value, name, title: d.title })
    if (d.title.includes('單位')) result.unit = name.length > 0 ? name : null
  }
  result.valid = valid
  return result
}

/** ProjectItemCodeService.ResolveRuleLookupKey（E／L 部分）：L 固定一份表；E 用第 7~8 碼當鍵。 */
function resolveResourceKey(kind, code) {
  if (kind === 'L') return { ruleKind: 'L', outline: 'L' }
  if (kind === 'E') return code.length >= 8 ? { ruleKind: 'E', outline: code.substring(6, 8) } : null
  return null
}

// 規則表 Map → 已換算好的碼位（同一份 Map 重複查同一章碼不重算；換一份 Map 自動重算）
const digitsCacheByRules = new WeakMap()

/**
 * BudgetItemPickerService.EvaluateRuleCore 的本機版。
 * @param {string} itemCode
 * @param {Map<string, object>} rules  `${ruleKind}:${章碼或資源鍵}` → 規則表（GetRuleTable 形狀）
 */
export function evaluateRule(itemCode, rules) {
  const code = String(itemCode ?? '').trim()
  if (!code) return emptyRule('')
  const parsed = parseItemCode(code)
  let cache = rules ? digitsCacheByRules.get(rules) : null
  if (rules && !cache) { cache = new Map(); digitsCacheByRules.set(rules, cache) }
  const digitsOf = (ruleKind, outline) => {
    if (!cache) return []
    const key = `${ruleKind}:${outline}`
    if (!cache.has(key)) cache.set(key, digitsFromTable(rules.get(key)))
    return cache.get(key)
  }
  if (parsed.kind === 'E' || parsed.kind === 'L') {
    const key = resolveResourceKey(parsed.kind, code)
    if (!key) return emptyRule(code.slice(0, 1))
    return buildRuleResult(code, key.outline, digitsOf(key.ruleKind, key.outline), '', '')
  }
  const chapter = parsed.outlineCode ?? (code.length >= 5 ? code.slice(0, 5) : code)
  if (!parsed.isChapterItem) return emptyRule(chapter)
  const ruleKind = parsed.prefix === 'W' ? 'W' : 'N'
  const digits = digitsOf(ruleKind, chapter)
  const rulePrefix = digits.length > 0 ? String(rules?.get(`${ruleKind}:${chapter}`)?.prefix ?? '').trimEnd() : ''
  return buildRuleResult(code, chapter, digits, parsed.prefix, rulePrefix)
}

/** 規則表清單（GetOfflineReferenceBundle 的 rules：[{ ruleKind, rule }]）→ evaluateRule 用的 Map。 */
export function rulesToMap(list) {
  return new Map((list || []).map((x) => [`${x.ruleKind}:${x.rule?.chapter ?? ''}`, x.rule]))
}

// ── 常用工項比率（原「編碼正確率」，2026-10-01 離線版）─────────────────────
// ⚠⚠⚠ 同構配對：BudgetCheckService.BuildCodeAccuracy／ClassifyAccuracyRow。兩邊讀同一份
//   tests/fixtures/offline-reference/accuracyCases.json（OfflineCodeAccuracyCrossTests.cs／
//   tests/unit/offlineReference.test.mjs），改一邊一定要改另一邊。

const ACCURACY_PATTERN_KEYS = ['outline_code', 'detail_code', 'code_length', 'unit_code_zero', 'unit', 'abnormal_code']
const ACCURACY_LABELS = {
  outline_code: '綱要編碼錯誤',
  detail_code: '細目編碼錯誤',
  code_length: '編碼長度錯誤',
  unit_code_zero: '單位碼不應為0',
  unit: '單位錯誤',
  abnormal_code: '非正常編碼',
}

/** BudgetCheckService.ClassifyAccuracyRow：{ accurate, detailAccurate, reasons }。 */
export function classifyAccuracyRow(code, unit, digits, codePrefix = '', rulePrefix = '') {
  const reasons = []
  if (code.length > 0 && 'EL'.includes(code[0].toUpperCase())) {
    reasons.push('abnormal_code')
    return { accurate: false, detailAccurate: false, reasons }
  }
  if (digits.length === 0) {
    reasons.push('outline_code')
    return { accurate: false, detailAccurate: false, reasons }
  }
  const chapter = code.length >= codePrefix.length + 5 ? code.substring(codePrefix.length, codePrefix.length + 5) : code
  const rule = buildRuleResult(code, chapter, digits, codePrefix, rulePrefix)
  if (!rule.valid) {
    const prefixLen = codePrefix.length
    const maxEnd = prefixLen + Math.max(...digits.map((d) => (d.digit - 1) + d.span))
    if (code.length !== maxEnd) reasons.push('code_length')
    const hasDigitMismatch = rule.prefixMismatch || digits.some((d) => {
      const start = prefixLen + (d.digit - 1)
      if (start < 0 || start + d.span > code.length) return false
      const value = code.slice(start, start + d.span)
      return d.codeNames.size > 0 && !d.codeNames.has(value)
    })
    if (hasDigitMismatch) reasons.push('detail_code')
  }
  const unitDigitIndex = codePrefix.length + 9
  if (code.length > unitDigitIndex && code[unitDigitIndex] === '0') reasons.push('unit_code_zero')
  if (rule.unit != null && unit !== rule.unit) reasons.push('unit')
  return { accurate: true, detailAccurate: rule.valid, reasons }
}

/** C# decimal 的 Math.Round(n/d*100, 1)（預設 MidpointRounding.ToEven）——用整數算，不經過浮點。 */
export function percentRoundHalfEven1(numerator, denominator) {
  const n = numerator * 1000
  let q = Math.floor(n / denominator)
  const r = n - q * denominator
  if (r * 2 > denominator || (r * 2 === denominator && q % 2 === 1)) q += 1
  return q / 10
}

/**
 * BudgetCheckService.BuildCodeAccuracy 的本機版。
 * @param {Array} allRows 估價單全部列（依畫面順序＝SortNo）：{ seq, nodeId, parentNodeId, itemNo, refItemCode, unit, descriptionZh }
 * @param {Map} rules evaluateRule 用的規則表 Map
 */
export function buildCodeAccuracy(allRows, rules) {
  const result = {
    accuracyRate: 0, detailAccuracyRate: 0, totalItemCount: 0, errorItemCount: 0,
    thresholds: { accuracy: 70, detail: 40 }, patterns: [], issues: [],
  }
  if (!allRows || allRows.length === 0) return result
  const parentIds = new Set(allRows.filter((x) => x.parentNodeId != null).map((x) => x.parentNodeId))
  const itemRows = allRows.filter((r) => !(r.parentNodeId == null || parentIds.has(r.nodeId)))
  const codedRows = itemRows.filter((r) => String(r.refItemCode ?? '').trim().length > 0)
  result.totalItemCount = codedRows.length
  if (codedRows.length === 0) return result

  let cache = rules ? digitsCacheByRules.get(rules) : null
  if (rules && !cache) { cache = new Map(); digitsCacheByRules.set(rules, cache) }
  const digitsOf = (ruleKind, outline) => {
    if (!cache) return []
    const key = `${ruleKind}:${outline}`
    if (!cache.has(key)) cache.set(key, digitsFromTable(rules.get(key)))
    return cache.get(key)
  }

  let correct = 0
  let detailCorrect = 0
  const patternCounts = {}
  for (const r of codedRows) {
    const code = String(r.refItemCode).trim()
    const unit = r.unit ?? ''
    const parsed = parseItemCode(code)
    const chapter = parsed.outlineCode ?? (code.length >= 5 ? code.slice(0, 5) : code)
    const ruleKind = parsed.prefix === 'W' ? 'W' : 'N'
    const digits = parsed.isChapterItem ? digitsOf(ruleKind, chapter) : []
    const rulePrefix = digits.length > 0 ? String(rules?.get(`${ruleKind}:${chapter}`)?.prefix ?? '').trimEnd() : ''
    const { accurate, detailAccurate, reasons } = classifyAccuracyRow(code, unit, digits, parsed.prefix, rulePrefix)
    if (accurate) correct += 1
    if (detailAccurate) detailCorrect += 1
    if (reasons.length > 0) {
      result.issues.push({ payItemSeq: r.seq, itemNo: r.itemNo ?? '', itemCode: code, name: r.descriptionZh ?? '', reasons })
      for (const k of reasons) patternCounts[k] = (patternCounts[k] || 0) + 1
    }
  }
  result.errorItemCount = codedRows.length - correct
  result.accuracyRate = percentRoundHalfEven1(correct, codedRows.length)
  result.detailAccuracyRate = percentRoundHalfEven1(detailCorrect, codedRows.length)
  result.patterns = ACCURACY_PATTERN_KEYS.map((k) => ({ key: k, label: ACCURACY_LABELS[k] ?? k, count: patternCounts[k] || 0 }))
  return result
}
